// ============================================================================
// RHCancelamentoAdmissao_Candidatos.js
// TODOS EM PERÍODO DE EXPERIÊNCIA (lista calculada ao vivo da dtbConfere, com a
// evidência do espelho de ponto ao lado). Renderizada pelo mini-hub
// RHCancelamentoAdmissao (aba do módulo RH).
//
// ⚠️ A régua mudou em 2026-09-15: era "recém-contratado que faltou na 1ª semana",
// virou "quem está em experiência". Quem decide o cancelamento é o GERENTE,
// olhando a evidência — o portal não pré-seleciona mais por falta, e por isso
// também não pergunta nada antes: o botão é direto.
//
// Dois públicos na MESMA tela (o recorte quem faz é o backend):
//   • Gerente (+ Admin) → vê a PRÓPRIA loja e ABRE o pedido com justificativa
//     obrigatória; o RH recebe por e-mail e decide.
//   • RH / Diretoria    → veem a REDE inteira em LEITURA. Não abrem pedido (o
//     POST /solicitar só aceita Gerente), então a última coluna vira situação.
//
// ⚠️ São ~280 linhas na visão da rede — por isso a tabela é PAGINADA e ordenável
// por qualquer coluna. Para o gerente, é a própria loja (9 a 45 pessoas).
// ============================================================================

import { useEffect, useState, useCallback, useMemo } from 'react';
import { UserX, AlertCircle, CalendarX2, Send, X, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { estilos, BadgeStatus } from '../../processoseletivo/psUI';

const COR = '#e11d48';
const POR_PAGINA = 20;

// Formata 'AAAA-MM-DD' → 'DD/MM/AAAA' (evita fuso do new Date com data pura).
const fmtData = (iso) => {
  if (!iso) return '—';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
};

// ⚠️ `null` = SEM DADO no espelho (recém-admitido ainda não entrou no CSV
// mensal), e isso NÃO é "zero falta". Mostrar 0 aqui faria o gerente achar que a
// pessoa está limpa quando ninguém sabe.
const fmtNumero = (n) => (n === null || n === undefined ? '—' : n);

// Fim de experiência que já passou deixou de ser um prazo a acompanhar: a célula
// vira travessão, igual a quem nunca teve a data preenchida. Mesmo comportamento
// do bloco "Em período de experiência" da Home (`DashboardRH.js` › expVigente) —
// é de lá que esta tela copia o formato, então copia também a regra.
//
// ⚠️ A pessoa CONTINUA na lista: quem a mantém lá é a 2ª experiência, ainda
// correndo. O que some é só a data vencida — normalmente a 1ª, que fecha em 30
// dias enquanto a 2ª vai até 90.
const HOJE_ISO = new Date().toISOString().slice(0, 10);
const expVigente = (iso) => {
  const d = iso ? String(iso).slice(0, 10) : null;
  return d && d >= HOJE_ISO ? d : null;
};

// Selo do status de um pedido já existente para o candidato.
const seloStatus = (status) => {
  if (status === 'PENDENTE') return <BadgeStatus tom="emProcesso">Pedido pendente</BadgeStatus>;
  if (status === 'APROVADO') return <BadgeStatus tom="aprovado">Término aprovado</BadgeStatus>;
  if (status === 'REPROVADO') return <BadgeStatus tom="reprovado">Pedido reprovado</BadgeStatus>;
  return null;
};

// Quem ABRE o pedido — espelha o PERFIL_SOLICITA do backend (+ bypass do Admin).
// RH/Diretoria entram nesta tela só para acompanhar.
const PERFIS_SOLICITAM = ['Gerente', 'Admin'];  // ⚠️ Operacional é o RH do Grupo Correia, que também pode abrir pedido

// As colunas da tabela, em ordem. `tipo` define como a coluna ORDENA — texto usa
// localeCompare (acento), número compara valor, e data ISO ordena como texto.
const COLUNAS = [
  { chave: 'loja', rotulo: 'Loja', tipo: 'texto' },
  { chave: 'nome', rotulo: 'Funcionário', tipo: 'texto' },
  { chave: 'cargo', rotulo: 'Cargo', tipo: 'texto' },
  { chave: 'dataAdmissao', rotulo: 'Admissão', tipo: 'data', numerico: true },
  { chave: 'exp1', rotulo: 'Fim 1ª Exp.', tipo: 'data', numerico: true },
  { chave: 'exp2', rotulo: 'Fim 2ª Exp.', tipo: 'data', numerico: true },
  { chave: 'faltas', rotulo: 'Faltas', tipo: 'numero', numerico: true },
  { chave: 'atestados', rotulo: 'Atestados', tipo: 'numero', numerico: true },
];

// Cabeçalho ordenável: as duas setas sempre visíveis (a ativa em destaque), para
// o usuário saber que a coluna ordena ANTES de clicar nela.
function ThOrdenavel({ coluna, ordem, setOrdem, alinhaDireita }) {
  const ativa = ordem.coluna === coluna.chave;
  return (
    <th
      style={{ ...estilos.th, cursor: 'pointer', userSelect: 'none', textAlign: alinhaDireita ? 'right' : 'left' }}
      onClick={() => setOrdem((o) => ({ coluna: coluna.chave, asc: o.coluna === coluna.chave ? !o.asc : true }))}
      title={`Ordenar por ${coluna.rotulo}`}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: alinhaDireita ? 'flex-end' : 'flex-start' }}>
        {coluna.rotulo}
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 0 }}>
          <ChevronUp size={11} style={{ color: ativa && ordem.asc ? COR : '#cbd5e1' }} />
          <ChevronDown size={11} style={{ color: ativa && !ordem.asc ? COR : '#cbd5e1', marginTop: -3 }} />
        </span>
      </span>
    </th>
  );
}

function CancelamentoCandidatos() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const podeSolicitar = PERFIS_SOLICITAM.includes(user?.perfil || '');

  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Modal de solicitação
  const [alvo, setAlvo] = useState(null);          // candidato selecionado p/ pedir cancelamento
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState(null);  // { tipo:'ok'|'erro', msg }

  const [filtroLoja, setFiltroLoja] = useState('');
  // Padrão: o prazo mais perto de vencer no topo — é o que tem decisão pendente.
  const [ordem, setOrdem] = useState({ coluna: 'exp2', asc: true });
  const [pagina, setPagina] = useState(1);

  // Lojas presentes na lista (o gerente só enxerga a própria; RH vê a rede toda).
  const lojas = useMemo(
    () => [...new Set(candidatos.map((c) => c.loja).filter(Boolean))].sort(),
    [candidatos]
  );

  // ⚠️ A data vencida é zerada AQUI, antes de filtrar e ordenar — não na hora de
  // desenhar a célula. Se fosse só na exibição, a coluna mostraria travessão mas
  // continuaria ordenando pela data escondida, e a ordem não bateria com o que
  // está na tela.
  const base = useMemo(
    () => candidatos.map((c) => ({ ...c, exp1: expVigente(c.exp1), exp2: expVigente(c.exp2) })),
    [candidatos]
  );

  const filtrados = useMemo(
    () => base.filter((c) => !filtroLoja || c.loja === filtroLoja),
    [base, filtroLoja]
  );

  const ordenados = useMemo(() => {
    const col = COLUNAS.find((c) => c.chave === ordem.coluna) || COLUNAS[0];
    const dir = ordem.asc ? 1 : -1;
    return [...filtrados].sort((a, b) => {
      const x = a[col.chave];
      const y = b[col.chave];
      // Sem valor (data em branco, ou falta/atestado sem dado no espelho) cai
      // sempre no FIM, nos dois sentidos: não há o que comparar, e quem tem
      // número é que interessa no topo.
      if (x === null || x === undefined || x === '') return 1;
      if (y === null || y === undefined || y === '') return -1;
      if (col.tipo === 'numero') return dir * (Number(x) - Number(y));
      if (col.tipo === 'data') return dir * String(x).localeCompare(String(y)); // ISO ordena como texto
      return dir * String(x).localeCompare(String(y), 'pt-BR');
    });
  }, [filtrados, ordem]);

  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);   // filtro que encurta a lista não deixa página órfã
  const visiveis = ordenados.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

  // Mudou o filtro ou a ordenação → volta para a primeira página (senão o
  // usuário fica olhando uma página vazia no fim da lista).
  useEffect(() => { setPagina(1); }, [filtroLoja, ordem]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/cancelamento-admissao/candidatos', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Falha ao carregar a lista.');
      setCandidatos(await resp.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirModal = (c) => {
    setAlvo(c);
    setJustificativa('');
    setFeedback(null);
  };
  const fecharModal = () => { if (!enviando) setAlvo(null); };

  const enviar = async () => {
    if (!alvo || !justificativa.trim() || enviando) return;
    setEnviando(true);
    setFeedback(null);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch('/api/rh/cancelamento-admissao/solicitar', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ idFuncionario: alvo.idFuncionario, justificativa: justificativa.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.message || 'Erro ao registrar solicitação.');
      setAlvo(null);
      await carregar();
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e.message });
    } finally {
      setEnviando(false);
    }
  };

  const botaoSolicitar = (c, rotulo = 'Solicitar Término de Contrato') => (
    <button
      onClick={() => abrirModal(c)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8,
        border: 'none', background: COR, color: '#fff', fontSize: 13, fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      <UserX size={14} /> {rotulo}
    </button>
  );

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ background: `${COR}15`, color: COR, padding: 12, borderRadius: 12, display: 'flex' }}>
          <CalendarX2 size={24} />
        </div>
        <div>
          {/* ⚠️ O nome que a rede lê é "Término do Contrato de Experiência"
              desde 16/09/2026 (pedido do RH). O ATO continua sendo cancelar —
              é o que o botão diz e o que o status grava —, mas o título tem de
              casar com o card que trouxe o usuário até aqui. */}
          <h3 style={estilos.tituloSecao}>Término do Contrato de Experiência</h3>
          <p style={estilos.subtituloSecao}>
            {podeSolicitar
              ? 'Funcionários da sua loja em período de experiência. Solicite o término do contrato de experiência quando for o caso — o RH decide.'
              : 'Funcionários da rede em período de experiência. Acompanhamento — quem abre o pedido é o gerente da loja.'}
          </p>
        </div>
      </div>

      {/* Barra de filtros — a de loja só aparece quando há mais de uma na lista
          (para o gerente, presa à própria loja, seria um select de opção única). */}
      {!loading && !erro && candidatos.length > 0 && (
        <div style={estilos.barraFiltros}>
          {lojas.length > 1 && (
            <div>
              <label style={estilos.rotuloFiltro} htmlFor="filtro-loja">Loja</label>
              <select id="filtro-loja" style={estilos.input} value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)}>
                <option value="">Todas as lojas</option>
                {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, paddingBottom: 10, fontVariantNumeric: 'tabular-nums' }}>
            {filtrados.length === candidatos.length
              ? `${candidatos.length} em experiência`
              : `${filtrados.length} de ${candidatos.length} em experiência`}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, color: '#64748b' }}>Carregando…</div>
      ) : erro ? (
        <div style={{ padding: 24, color: COR, display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={18} /> {erro}</div>
      ) : candidatos.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
          Ninguém em período de experiência no momento.
        </div>
      ) : filtrados.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>
          Nenhum funcionário com os filtros selecionados.
        </div>
      ) : (
        <>
          <div style={{ ...estilos.tabelaWrap, overflowX: 'auto' }}>
            <table style={{ ...estilos.tabela, minWidth: 1040 }}>
              <thead>
                <tr>
                  {COLUNAS.map((col) => (
                    <ThOrdenavel key={col.chave} coluna={col} ordem={ordem} setOrdem={setOrdem} alinhaDireita={col.numerico} />
                  ))}
                  <th style={{ ...estilos.th, textAlign: 'right' }}>{podeSolicitar ? 'Ação' : 'Situação'}</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((c) => {
                  const num = { ...estilos.td, fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
                  return (
                    <tr key={c.idFuncionario}>
                      <td style={estilos.td}>{c.loja || '—'}</td>
                      <td style={estilos.td}>
                        <strong>{c.nome}</strong>
                        {c.cpf && <div style={{ fontSize: 12, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{c.cpf}</div>}
                      </td>
                      <td style={estilos.td}>{c.cargo || '—'}</td>
                      <td style={num}>{fmtData(c.dataAdmissao)}</td>
                      <td style={num}>{fmtData(c.exp1)}</td>
                      <td style={num}>{fmtData(c.exp2)}</td>
                      <td style={num}>{fmtNumero(c.faltas)}</td>
                      <td style={num}>{fmtNumero(c.atestados)}</td>
                      <td style={{ ...estilos.td, textAlign: 'right' }}>
                        {!podeSolicitar ? (
                          // RH/Diretoria: leitura. Mostra só onde o caso está.
                          seloStatus(c.solicitacaoStatus) || (
                            <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Sem pedido</span>
                          )
                        ) : c.jaSolicitado ? (
                          // Já tem pedido em aberto (PENDENTE) ou aprovado: só o selo.
                          seloStatus(c.solicitacaoStatus)
                        ) : c.solicitacaoStatus === 'REPROVADO' ? (
                          // Reprovado antes: mostra o selo e deixa reabrir (nova evidência).
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                            {seloStatus('REPROVADO')}
                            {botaoSolicitar(c, 'Reabrir')}
                          </div>
                        ) : (
                          botaoSolicitar(c)
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginação — some quando tudo cabe numa página só. */}
          {totalPaginas > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, marginTop: 14, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {(paginaAtual - 1) * POR_PAGINA + 1}–{Math.min(paginaAtual * POR_PAGINA, ordenados.length)} de {ordenados.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  disabled={paginaAtual === 1}
                  style={botaoPagina(paginaAtual === 1)}
                >
                  <ChevronLeft size={16} /> Anterior
                </button>
                <span style={{ fontSize: 13, color: '#334155', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {paginaAtual} / {totalPaginas}
                </span>
                <button
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  disabled={paginaAtual === totalPaginas}
                  style={botaoPagina(paginaAtual === totalPaginas)}
                >
                  Próxima <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de justificativa */}
      {alvo && (
        <div
          onClick={fecharModal}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 20px 40px rgba(15,23,42,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Solicitação de Término de Contrato de Experiência</h3>
              <button onClick={fecharModal} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
              <div><strong>{alvo.nome}</strong> · {alvo.cargo || '—'} · {alvo.loja || '—'}</div>
              <div style={{ marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
                Admitido em {fmtData(alvo.dataAdmissao)} · experiência até {fmtData(alvo.exp2)}
              </div>
              <div style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                {fmtNumero(alvo.faltas)} falta(s) · {fmtNumero(alvo.atestados)} atestado(s) desde a admissão
              </div>
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              Justificativa <span style={{ color: COR }}>*</span>
            </label>
            <textarea
              autoFocus
              style={{ width: '100%', minHeight: 96, padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#0f172a', boxSizing: 'border-box', resize: 'vertical' }}
              placeholder="Explique o motivo do pedido de término do contrato de experiência (obrigatório)…"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
            />

            {feedback && feedback.tipo === 'erro' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 14, fontWeight: 600 }}>
                <AlertCircle size={18} /> {feedback.msg}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button
                onClick={fecharModal}
                disabled={enviando}
                style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 14, cursor: enviando ? 'not-allowed' : 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={enviar}
                disabled={!justificativa.trim() || enviando}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, border: 'none',
                  fontWeight: 700, fontSize: 14, color: '#fff',
                  background: justificativa.trim() && !enviando ? COR : '#f1d5da',
                  cursor: justificativa.trim() && !enviando ? 'pointer' : 'not-allowed',
                }}
              >
                <Send size={16} /> {enviando ? 'Enviando…' : 'Enviar ao RH'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const botaoPagina = (desabilitado) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff',
  color: desabilitado ? '#cbd5e1' : '#334155',
  fontSize: 13, fontWeight: 700, cursor: desabilitado ? 'not-allowed' : 'pointer',
});

export default CancelamentoCandidatos;
